import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useAnalytics } from "../../utils/Customer/useAnalytics";

interface PeekCarouselItem {
  id: number;
  imageUrl: string;
}

interface PeekCarouselProps {
  images: PeekCarouselItem[];
  handleImageClick?: (index: number, imageUrl: string) => void;
}

const PeekCarousel: React.FC<PeekCarouselProps> = ({
  images,
  handleImageClick,
}) => {
  const { trackBannerView, trackBannerClick } = useAnalytics();

  const [current, setCurrent] = useState(0);
  const [itemsToShow, setItemsToShow] = useState(80);

  const bannerRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const [visibleBannerIds, setVisibleBannerIds] = useState<number[]>([]);

  const total = images.length;

  // ------------------------------------------------------------
  // Responsive layout
  // ------------------------------------------------------------

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setItemsToShow(100);
      } else if (window.innerWidth < 1024) {
        setItemsToShow(90);
      } else {
        setItemsToShow(80);
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // ------------------------------------------------------------
  // Auto play
  // ------------------------------------------------------------

  useEffect(() => {
    if (total <= 1) {
      return;
    }

    const interval = setInterval(() => {
      setCurrent((prev) => (prev === total - 1 ? 0 : prev + 1));
    }, 3000);

    return () => clearInterval(interval);
  }, [total]);

  // ------------------------------------------------------------
  // Banner visibility
  // ------------------------------------------------------------

  useEffect(() => {
    if (!images.length) {
      return;
    }

    const observers: IntersectionObserver[] = [];

    images.forEach((banner) => {
      const element = bannerRefs.current[banner.id];

      if (!element) {
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          setVisibleBannerIds((previous) => {
            if (entry.isIntersecting) {
              if (previous.includes(banner.id)) {
                return previous;
              }

              return [...previous, banner.id];
            }

            return previous.filter((id) => id !== banner.id);
          });
        },
        {
          threshold: 0.5,
        },
      );

      observer.observe(element);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => {
        observer.disconnect();
      });
    };
  }, [images]);

  // ------------------------------------------------------------
  // Banner View
  // ------------------------------------------------------------

  useEffect(() => {
    visibleBannerIds.forEach((bannerId) => {
      trackBannerView(bannerId);
    });
  }, [visibleBannerIds, trackBannerView]);

  // ------------------------------------------------------------
  // Navigation
  // ------------------------------------------------------------

  const next = () => {
    if (current < total - 1) {
      setCurrent(current + 1);
    }
  };

  const prev = () => {
    if (current > 0) {
      setCurrent(current - 1);
    }
  };

  // ------------------------------------------------------------
  // Translation
  // ------------------------------------------------------------

  const getTranslate = () => {
    if (itemsToShow === 100) {
      return -(current * 100);
    }

    if (current === 0) {
      return 0;
    }

    if (current === total - 1) {
      return -(current * itemsToShow - (100 - itemsToShow));
    }

    return -(current * itemsToShow - (100 - itemsToShow) / 2);
  };

  // ------------------------------------------------------------
  // Banner click
  // ------------------------------------------------------------

  const handleBannerClick = (index: number, banner: PeekCarouselItem) => {
    if (banner.id) {
      trackBannerClick(banner.id);
    }

    handleImageClick?.(index, banner.imageUrl);
  };

  if (!total) {
    return null;
  }

  return (
    <div className="w-full overflow-hidden relative">
      {/* Slider */}
      <div
        className="flex transition-transform duration-500 ease-in-out"
        style={{
          transform: `translateX(${getTranslate()}%)`,
        }}
      >
        {images.map((banner, index) => (
          <div
            key={banner.id}
            ref={(element) => {
              bannerRefs.current[banner.id] = element;
            }}
            className="flex-shrink-0 px-2"
            style={{
              width: `${itemsToShow}%`,
            }}
            onClick={() => handleBannerClick(index, banner)}
          >
            <img
              src={banner.imageUrl}
              alt={`slide-${index}`}
              className="
                w-full
                h-[220px]
                sm:h-[260px]
                md:h-[280px]
                lg:h-[300px]
                object-cover
                rounded-xl
              "
            />
          </div>
        ))}
      </div>

      {/* Controls */}
      <button
        onClick={prev}
        className="
          absolute
          left-2
          top-1/2
          -translate-y-1/2
          bg-white
          shadow
          p-2
          rounded-full
          hover:bg-gray-100
        "
      >
        <ArrowLeft size={20} />
      </button>

      <button
        onClick={next}
        className="
          absolute
          right-2
          top-1/2
          -translate-y-1/2
          bg-white
          shadow
          p-2
          rounded-full
          hover:bg-gray-100
        "
      >
        <ArrowRight size={20} />
      </button>
    </div>
  );
};

export default PeekCarousel;
